<template>
	<VerticalLayout>
		{{ $t('dateRangeChooser.' + (showTime ? 'dateTimesRangeLabel' : 'datesRangeLabel')) }}
		<RangePicker
			v-model:value="datesRangeValue"
			:showTime="showTime"
			:format="dateFormat"
			:getPopupContainer="getPopupContainer"
			@change="onDatesRange"
		/>

		{{ $t('dateRangeChooser.quickPeriod.label') }}
		<Select
			class="width-100"
			v-model:value="quickPeriodLocal"
			:options="quickPeriodOptions"
			@change="quickPeriodChange"
			:getPopupContainer="getPopupContainer"
		/>
	</VerticalLayout>
</template>

<script>
import { ref, toRef, computed, nextTick, onMounted } from 'vue';
import { RangePicker, Select } from 'ant-design-vue';

import { getTodayDayJs, getNowDayJs, DATE_FORMAT, DATETIME_FORMAT } from '@/components/common/dateUtils';

import { usePopupContainer } from '@/utils/usePopupContainer';

import VerticalLayout from '@/components/common/VerticalLayout';

import TranslationUtils from '@/i18n/TranslationUtils';


const QUICK_PERIOD_DAY_MODE = [
	'user', 'day1', 'day2', 'week1', 'week2', 'month1', 'month2', 'month3',
];

const QUICK_PERIOD_DATETIME_MODE = [
	'user', 'minute5', 'minute15', 'minute30', 'hour1', 'hour2', 'hour6', 'hour12', 'hour24',
];

const PERIODS = [
	{ value: 'user', count: 0, unit: 'day' },
	{ value: 'day1', count: 0, unit: 'day' },
	{ value: 'day2', count: 1, unit: 'day' },
	{ value: 'week1', count: 6, unit: 'day' },
	{ value: 'week2', count: 13, unit: 'day' },
	{ value: 'month1', count: 1, unit: 'month' },
	{ value: 'month2', count: 2, unit: 'month' },
	{ value: 'month3', count: 3, unit: 'month' },
	{ value: 'minute5', count: 5, unit: 'minute' },
	{ value: 'minute15', count: 15, unit: 'minute' },
	{ value: 'minute30', count: 30, unit: 'minute' },
	{ value: 'hour1', count: 1, unit: 'hour' },
	{ value: 'hour2', count: 2, unit: 'hour' },
	{ value: 'hour6', count: 6, unit: 'hour' },
	{ value: 'hour12', count: 12, unit: 'hour' },
	{ value: 'hour24', count: 24, unit: 'hour' },
];


const calcStartDate = (endDate, quickPeriod, periods) => {
	const substractData = periods.filter(item => item.value === quickPeriod)[0];
	return endDate.subtract(substractData.count, substractData.unit);
};

const getPeriodSettings = selected => {
	return selected.map(value => {
		const foundPeriods = PERIODS.filter(item => item.value === value);
		if (foundPeriods.length === 0) {
			return null;
		}
		return foundPeriods[0];
	}).filter(item => item != null);
}


export default {
	components: {
		RangePicker, Select,
		VerticalLayout,
	},
	emits: ['update:modelValue', 'change'],
	props: {
		// `value` из словаря QUICK_PERIOD_OPTIONS_ITEMS
		quickPeriod: {
			type: String,
			default: 'user',
			required: false,
		},
		// пустой массив или массив из двух элементов daysjs
		modelValue: {
			type: Array,
			default() {
				return [];
			}
		},
		showTime: {
			type: Boolean,
			default: false,
		},
		quickPeriods: {
			type: Array,
			default: () => [],
			required: false,
		}
	},
	setup(props, { emit, expose }) {
		const modelValue = toRef(props, 'modelValue');
		const showTimeRef = toRef(props, 'showTime');
		const quickPeriodLocal = ref(props.quickPeriod);
		const quickPeriodsRef = toRef(props, 'quickPeriods');
		const quickPeriods = computed(() => {
			if (quickPeriodsRef.value.length > 0) {
				const usedPeriods = quickPeriodsRef.value.includes('user') ? quickPeriodsRef.value : ['user', ...quickPeriodsRef.value];
				return getPeriodSettings(usedPeriods);
			}
			return getPeriodSettings(showTimeRef.value ? QUICK_PERIOD_DATETIME_MODE : QUICK_PERIOD_DAY_MODE);
		});
		const quickPeriodOptions = TranslationUtils.addTranslatedField(
			quickPeriods.value,
			'value',
			'label',
			'dateRangeChooser.quickPeriod',
		);
		const datesRangeValue = computed({
			get: () => {
				if (modelValue.value == null) {
					return null;
				}
				if (modelValue.value.length == 2) {
					return modelValue.value;
				}
				return null;
			},
			set: value => {
				emit('update:modelValue', value);
				emit('change', value);
			},
		});
		const dateFormat = computed(() => {
			return showTimeRef.value ? DATETIME_FORMAT : DATE_FORMAT;
		});

		const onDatesRange = () => {
			quickPeriodLocal.value = 'user';
		};

		const quickPeriodChange = () => {
			nextTick(() => {
				if (quickPeriodLocal.value === 'user') {
					return;
				}
				const endDate = showTimeRef.value ? getNowDayJs() : getTodayDayJs();
				datesRangeValue.value = [
					calcStartDate(endDate, quickPeriodLocal.value, quickPeriods.value),
					endDate,
				];
			});
		};
		const setEndDateNow = () => {
			if (datesRangeValue.value != null && datesRangeValue.value.length === 2) {
				datesRangeValue.value = [datesRangeValue.value[0], showTimeRef.value ? getNowDayJs() : getTodayDayJs()];
			}
		};
		expose({ setEndDateNow });
		onMounted(() => {
			quickPeriodChange();
		});
		const { getPopupContainer } = usePopupContainer();
		return {
			quickPeriodLocal,
			quickPeriodOptions,
			datesRangeValue,
			onDatesRange,
			quickPeriodChange,
			dateFormat,
			getPopupContainer,
		};
	},
}
</script>
